import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type {
  ProviderSourceState,
  TrackedEntity,
  TrackedRoute,
} from '../types'

export interface TrackingProviderSnapshot {
  sources: Record<string, ProviderSourceState>
  entities: Record<string, TrackedEntity[]>
  routes: Record<string, TrackedRoute[]>
  defaultSourceKey?: string | null
}

const EMPTY_SOURCE: ProviderSourceState = {
  sourceKey: '',
  status: 'unconfigured',
  lastFreshAt: null,
  errorCode: null,
}
const EMPTY_ENTITIES: TrackedEntity[] = []
const EMPTY_ROUTES: TrackedRoute[] = []

const TrackingContext = createContext<TrackingProviderSnapshot | null>(null)

export function TrackingProvider({
  snapshot,
  children,
}: {
  snapshot?: TrackingProviderSnapshot
  children: ReactNode
}) {
  const value = useMemo<TrackingProviderSnapshot>(
    () =>
      snapshot ?? {
        sources: {},
        entities: {},
        routes: {},
        defaultSourceKey: null,
      },
    [snapshot],
  )
  return <TrackingContext.Provider value={value}>{children}</TrackingContext.Provider>
}

function useTrackingContext(): TrackingProviderSnapshot {
  const context = useContext(TrackingContext)
  if (!context) throw new Error('Tracking hooks must be used inside <TrackingProvider>.')
  return context
}

function resolveSourceKey(
  context: TrackingProviderSnapshot,
  sourceKey?: string,
): string {
  return sourceKey ?? context.defaultSourceKey ?? ''
}

export function useTrackingSource(sourceKey?: string): ProviderSourceState {
  const context = useTrackingContext()
  const resolved = resolveSourceKey(context, sourceKey)
  return context.sources[resolved] ?? { ...EMPTY_SOURCE, sourceKey: resolved }
}

export function useTrackedEntities(sourceKey?: string): TrackedEntity[] {
  const context = useTrackingContext()
  return context.entities[resolveSourceKey(context, sourceKey)] ?? EMPTY_ENTITIES
}

export function useRoutes(sourceKey?: string): TrackedRoute[] {
  const context = useTrackingContext()
  return context.routes[resolveSourceKey(context, sourceKey)] ?? EMPTY_ROUTES
}
